import React, { useEffect, useRef, useState } from "react";
import { getPosts, getPost, relatedPosts } from "./blog";
import type { ResolvedPost } from "./blog/types";
import { getReleases, getRelease, classifyRelease, LEVEL_STYLE, type Release } from "./blog/releases";
import { useLocale, useT } from "./i18n/context";
import { formatDate } from "./i18n/format";

/** Client-side navigate to an internal path (SPA history + popstate). */
export function navigate(to: string) {
  if (window.location.pathname === to) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0 });
}

function MetaLine({ post }: { post: ResolvedPost }) {
  const { locale } = useLocale();
  return (
    <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
      {formatDate(locale, post.date)}
      {post.readingTime ? ` · ${post.readingTime}` : ""}
    </span>
  );
}

/** Tags as quiet inline text — no pills. */
function TagText({ tags }: { tags?: string[] }) {
  if (!tags?.length) return null;
  return <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{tags.join(" · ")}</span>;
}

/** The big lead article — a large cover image with the title + text below it. */
function HeroFeature({ post }: { post: ResolvedPost }) {
  const t = useT();
  return (
    <a
      href={`/blog/${post.slug}`}
      onClick={(e) => { e.preventDefault(); navigate(`/blog/${post.slug}`); }}
      className="blog-hero"
      style={{
        display: "block",
        textDecoration: "none",
        overflow: "hidden",
        borderRadius: 12,
        backgroundColor: "var(--surface-5)",
        border: "1px solid var(--border-strong)",
        transition: "border-color 220ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--border-focus)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; }}
    >
      {post.hero && (
        <div className="blog-hero-img">
          <img src={post.hero} alt={post.heroAlt || post.title} className="blog-hero-photo" />
        </div>
      )}
      <div className="blog-hero-body">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#5ba8f5" }}>{t.blog.index.latest}</span>
          <MetaLine post={post} />
        </div>
        <h2 className="blog-hero-title" style={{ margin: "0 0 14px", fontWeight: 600, letterSpacing: "-0.025em", color: "var(--text-primary)" }}>
          {post.title}
        </h2>
        <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: "var(--text-secondary)", maxWidth: 760 }}>
          {post.excerpt}
        </p>
      </div>
    </a>
  );
}

/** A single version in the releases ticker — links to its auto-generated release page. */
function VersionChip({ r }: { r: Release }) {
  const level = classifyRelease(r.version);
  const s = LEVEL_STYLE[level];
  return (
    <a
      href={`/blog/release/${r.version}`}
      onClick={(e) => { e.preventDefault(); navigate(`/blog/release/${r.version}`); }}
      title={`${r.title} · v${r.version}`}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 14,
        fontWeight: s.weight,
        color: s.color,
        opacity: s.opacity,
        whiteSpace: "nowrap",
        textDecoration: "none",
        borderBottom: "1px solid transparent",
        paddingBottom: 1,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderBottomColor = `${s.color}99`)}
      onMouseLeave={(e) => (e.currentTarget.style.borderBottomColor = "transparent")}
    >
      v{r.version}
    </a>
  );
}

/** An auto-spinning version ticker — pauses on hover, draggable and wheel-scrollable. */
function ReleasesStrip() {
  const { locale } = useLocale();
  const t = useT();
  const releases = getReleases(locale);
  const trackRef = useRef<HTMLDivElement>(null);
  const st = useRef({ paused: false, dragging: false, captured: false, pointerId: 0, startX: 0, startScroll: 0, moved: false, pos: 0 });

  // Continuous auto-scroll; wraps at the halfway point (content is duplicated).
  // A float accumulator drives scrollLeft so sub-pixel speed isn't rounded away.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    const tick = () => {
      const s = st.current;
      const half = el.scrollWidth / 2;
      if (!reduce && !s.paused && !s.dragging && half > 0) {
        s.pos += 0.5;
        if (s.pos >= half) s.pos -= half;
        el.scrollLeft = s.pos;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const setScroll = (v: number) => {
    const el = trackRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    let p = v;
    if (half > 0) {
      if (p >= half) p -= half;
      else if (p < 0) p += half;
    }
    st.current.pos = p;
    el.scrollLeft = p;
  };

  const onDown = (e: React.PointerEvent) => {
    const el = trackRef.current;
    if (!el) return;
    const s = st.current;
    s.dragging = true;
    s.moved = false;
    s.captured = false;
    s.pointerId = e.pointerId;
    s.startX = e.clientX;
    s.startScroll = el.scrollLeft;
    // Don't capture yet — let a plain click reach the version link underneath.
  };
  const onMove = (e: React.PointerEvent) => {
    const s = st.current;
    const el = trackRef.current;
    if (!s.dragging || !el) return;
    if (Math.abs(e.clientX - s.startX) > 4) {
      s.moved = true;
      if (!s.captured) {
        try { el.setPointerCapture(s.pointerId); } catch {}
        s.captured = true;
      }
    }
    if (s.moved) setScroll(s.startScroll - (e.clientX - s.startX));
  };
  const onUp = () => {
    const s = st.current;
    if (s.captured) { try { trackRef.current?.releasePointerCapture(s.pointerId); } catch {} }
    s.dragging = false;
    s.captured = false;
  };
  const onWheel = (e: React.WheelEvent) => {
    setScroll(st.current.pos + e.deltaY + e.deltaX);
  };
  // Swallow click after a drag so a dragged version doesn't navigate.
  const onClickCapture = (e: React.MouseEvent) => {
    if (st.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      st.current.moved = false;
    }
  };

  return (
    <div className="blog-releases">
      <span className="blog-releases-label">{t.blog.index.releases}</span>
      <div
        ref={trackRef}
        className="blog-releases-track"
        onPointerEnter={() => (st.current.paused = true)}
        onPointerLeave={() => { st.current.paused = false; st.current.dragging = false; }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onWheel={onWheel}
        onClickCapture={onClickCapture}
      >
        {[...releases, ...releases].map((r, i) => (
          <span className="blog-releases-item" key={`${r.version}-${i}`}>
            <VersionChip r={r} />
          </span>
        ))}
      </div>
      <a
        href="https://github.com/0-AI-UG/cate/releases"
        target="_blank"
        rel="noopener noreferrer"
        className="blog-releases-all"
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
      >
        {t.blog.index.releasesAll}
      </a>
    </div>
  );
}

type NodePos = { x: number; y: number; r: number; z: number };

/** Past articles as draggable window-nodes on a mini canvas (echoes the product). */
function ArticleCanvas({ items, minNodes = 6 }: { items: ResolvedPost[]; minNodes?: number }) {
  const { locale } = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<NodePos[]>([]);
  const [cardW, setCardW] = useState(300);
  const [height, setHeight] = useState(540);
  // Real posts first, then empty "ghost" slots so the canvas never looks bare;
  // each ghost fills with a real article as soon as one is published.
  const total = Math.max(items.length, minNodes);
  const zTop = useRef(total);
  const drag = useRef<
    | null
    | { i: number; offX: number; offY: number; left: number; top: number; moved: boolean; sx: number; sy: number }
  >(null);

  // Lay the nodes out once we can measure the canvas width.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const W = el.clientWidth;
    const small = W < 680;
    const w = small ? Math.min(300, W - 20) : 272;
    const gapX = small ? 16 : 26;
    const cols = Math.max(1, Math.min(total, Math.floor((W - 8) / (w + gapX)) || 1));
    const rowH = small ? 332 : 304;
    const next: NodePos[] = Array.from({ length: total }, (_, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const jx = ((i * 53) % 19) - 9; // deterministic jitter — no Math.random
      const jy = ((i * 89) % 17) - 8;
      const rot = small ? 0 : (((i * 41) % 9) - 4) * 0.7; // ~ -2.8°..2.8°
      return { x: 12 + col * (w + gapX) + jx, y: 18 + row * rowH + jy, r: rot, z: i + 1 };
    });
    zTop.current = total;
    setCardW(w);
    setPos(next);
    const rows = Math.ceil(total / cols);
    setHeight(18 + rows * rowH + 44);
  }, [total]);

  const onDown = (e: React.PointerEvent, i: number) => {
    const el = ref.current;
    if (!el || !pos[i]) return;
    const rect = el.getBoundingClientRect();
    drag.current = {
      i,
      offX: e.clientX - rect.left - pos[i].x,
      offY: e.clientY - rect.top - pos[i].y,
      left: rect.left,
      top: rect.top,
      moved: false,
      sx: e.clientX,
      sy: e.clientY,
    };
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const nz = ++zTop.current;
    setPos((prev) => prev.map((q, idx) => (idx === i ? { ...q, z: nz } : q)));
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = ref.current;
    if (!d || !el) return;
    if (Math.abs(e.clientX - d.sx) > 4 || Math.abs(e.clientY - d.sy) > 4) d.moved = true;
    const W = el.clientWidth;
    const H = el.clientHeight;
    const nx = Math.max(-40, Math.min(e.clientX - d.left - d.offX, W - 80));
    const ny = Math.max(-16, Math.min(e.clientY - d.top - d.offY, H - 56));
    setPos((prev) => prev.map((q, idx) => (idx === d.i ? { ...q, x: nx, y: ny } : q)));
  };

  const onUp = (slug: string | null) => {
    const d = drag.current;
    drag.current = null;
    if (slug && d && !d.moved) navigate(`/blog/${slug}`);
  };

  const ghostH = Math.round(cardW * (9 / 16)) + 96;

  return (
    <div ref={ref} className="article-canvas" style={{ height }}>
      {Array.from({ length: total }, (_, i) => {
        const post = items[i];
        const p = pos[i];
        const common = {
          left: p ? p.x : 0,
          top: p ? p.y : 0,
          width: cardW,
          zIndex: p ? p.z : 1,
          transform: `rotate(${p ? p.r : 0}deg)`,
          opacity: p ? 1 : 0,
        };

        // Empty slot — a dashed ghost card that future posts will fill.
        if (!post) {
          return (
            <div
              key={`ghost-${i}`}
              className="article-node article-ghost"
              style={{ ...common, height: ghostH }}
              onPointerDown={(e) => onDown(e, i)}
              onPointerMove={onMove}
              onPointerUp={() => onUp(null)}
            >
              <div className="article-ghost-img" />
              <div className="article-node-body">
                <div className="article-ghost-bar" style={{ width: "38%" }} />
                <div className="article-ghost-bar" style={{ width: "88%", height: 11, marginTop: 11 }} />
                <div className="article-ghost-bar" style={{ width: "66%", marginTop: 8 }} />
              </div>
            </div>
          );
        }

        return (
          <div
            key={post.slug}
            className="article-node window-chrome"
            style={common}
            onPointerDown={(e) => onDown(e, i)}
            onPointerMove={onMove}
            onPointerUp={() => onUp(post.slug)}
          >
            {post.hero && (
              <div className="article-node-img">
                <img src={post.hero} alt={post.heroAlt || post.title} draggable={false} />
              </div>
            )}
            <div className="article-node-body">
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)" }}>
                {formatDate(locale, post.date)}
                {post.readingTime ? ` · ${post.readingTime}` : ""}
              </span>
              <h3 style={{ margin: "7px 0 0", fontSize: 16, lineHeight: 1.3, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
                {post.title}
              </h3>
              <p className="article-node-excerpt">{post.excerpt}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function BlogIndex() {
  const { locale } = useLocale();
  const t = useT();
  const posts = getPosts(locale);

  useEffect(() => {
    document.title = `${t.blog.index.title} · CATE`;
  }, [t]);

  const [featured] = posts;

  return (
    <section className="px-6 sm:px-8" style={{ maxWidth: 1040, margin: "0 auto", paddingTop: 36, paddingBottom: 96 }}>
      <header style={{ marginBottom: 28 }}>
        <h1
          className="fade-up"
          style={{ margin: "0 0 12px", fontSize: 46, fontWeight: 600, letterSpacing: "-0.02em", color: "var(--text-primary)" }}
        >
          {t.blog.index.title}
        </h1>
        <p className="fade-up" style={{ margin: 0, fontSize: 17, color: "var(--text-secondary)", animationDelay: "0.05s" }}>
          {t.blog.index.subtitle}
        </p>
      </header>

      {!featured ? (
        <p style={{ color: "var(--text-secondary)" }}>{t.blog.index.empty}</p>
      ) : (
        <>
          <div className="fade-up">
            <HeroFeature post={featured} />
          </div>

          <div className="fade-up" style={{ animationDelay: "0.08s", marginTop: 32 }}>
            <ReleasesStrip />
          </div>

          <div className="fade-up" style={{ animationDelay: "0.12s", marginTop: 44 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
                {t.blog.index.pastArticles}
              </h2>
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{t.blog.index.pastHint}</span>
            </div>
            <ArticleCanvas items={posts} />
          </div>
        </>
      )}
    </section>
  );
}

/** A plain, bright post card — used for related posts at the foot of an entry. */
function PostCard({ post }: { post: ResolvedPost }) {
  return (
    <a
      href={`/blog/${post.slug}`}
      onClick={(e) => { e.preventDefault(); navigate(`/blog/${post.slug}`); }}
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 12,
        textDecoration: "none",
        backgroundColor: "var(--surface-5)",
        border: "1px solid var(--border-strong)",
        transition: "border-color 200ms ease, transform 200ms ease, background-color 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.backgroundColor = "var(--surface-6)";
        e.currentTarget.style.borderColor = "var(--border-focus)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.backgroundColor = "var(--surface-5)";
        e.currentTarget.style.borderColor = "var(--border-strong)";
      }}
    >
      {post.hero && (
        <div style={{ position: "relative", aspectRatio: "16 / 9", backgroundColor: "var(--surface-4)" }}>
          <img
            src={post.hero}
            alt={post.heroAlt || post.title}
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
      )}
      <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ marginBottom: 8 }}>
          <MetaLine post={post} />
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, lineHeight: 1.3, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          {post.title}
        </h3>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--text-secondary)" }}>
          {post.excerpt}
        </p>
      </div>
    </a>
  );
}

function RelatedPosts({ slug }: { slug: string }) {
  const { locale } = useLocale();
  const t = useT();
  const related = relatedPosts(locale, slug, 3);
  if (!related.length) return null;
  return (
    <section style={{ marginTop: 56 }}>
      <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
        {t.blog.post.keepReading}
      </h2>
      <div className="blog-related-grid">
        {related.map((p) => (
          <PostCard key={p.slug} post={p} />
        ))}
      </div>
    </section>
  );
}

/**
 * Auto-generated release page at `/blog/release/<version>`. Renders purely from
 * the `releases` data with a distinct, changelog-style look (colored by level) —
 * adding a release entry publishes this page with no extra files.
 */
export function ReleaseArticlePage({ version }: { version: string }) {
  const { locale } = useLocale();
  const t = useT();
  const release = getRelease(locale, version);

  useEffect(() => {
    document.title = release ? `CATE v${version} · ` : `${t.blog.release.notFoundTitle} · CATE`;
  }, [release, version, t]);

  if (!release) {
    return (
      <section className="px-6" style={{ maxWidth: 720, margin: "0 auto", paddingTop: 64, paddingBottom: 96, textAlign: "center" }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 }}>{t.blog.release.notFoundTitle}</h1>
        <p style={{ color: "var(--text-secondary)", marginBottom: 24 }}>{t.blog.release.notFoundBody.replace("{version}", version)}</p>
        <a href="/blog" onClick={(e) => { e.preventDefault(); navigate("/blog"); }} style={{ color: "#5ba8f5", fontSize: 15 }}>{t.blog.release.notFoundBack}</a>
      </section>
    );
  }

  const level = classifyRelease(version);
  const s = LEVEL_STYLE[level];
  const levelLabel = level === "major" ? t.blog.release.levelMajor : level === "minor" ? t.blog.release.levelMinor : t.blog.release.levelPatch;
  const releases = getReleases(locale);
  const idx = releases.findIndex((r) => r.version === version);
  const newer = idx > 0 ? releases[idx - 1] : undefined;
  const older = idx >= 0 && idx < releases.length - 1 ? releases[idx + 1] : undefined;
  const post = release.postSlug ? getPost(locale, release.postSlug) : undefined;
  const tagUrl = `https://github.com/0-AI-UG/cate/releases/tag/v${version}`;

  return (
    <article className="px-6" style={{ maxWidth: 720, margin: "0 auto", paddingTop: 24, paddingBottom: 96 }}>
      <a
        href="/blog"
        onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
        className="fade-up"
        style={{ display: "inline-block", marginBottom: 24, fontSize: 14, color: "var(--text-secondary)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
      >
        {t.blog.post.back}
      </a>

      {/* Release header — colored by level, version-forward */}
      <div
        className="fade-up"
        style={{
          animationDelay: "0.05s",
          borderRadius: 12,
          border: `1px solid ${s.color}40`,
          background: `linear-gradient(135deg, ${s.color}24, transparent 62%)`,
          padding: "26px 28px 28px",
          marginBottom: 28,
        }}
      >
        <span
          style={{
            display: "inline-block",
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: s.color,
            border: `1px solid ${s.color}66`,
            borderRadius: 999,
            padding: "3px 10px",
            marginBottom: 14,
          }}
        >
          {levelLabel}
        </span>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 40,
            fontWeight: 700,
            lineHeight: 1,
            color: s.color,
            marginBottom: 10,
          }}
        >
          v{version}
        </div>
        <div style={{ fontSize: 13.5, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
          {formatDate(locale, release.date)}
        </div>
      </div>

      <h1
        className="fade-up"
        style={{ animationDelay: "0.1s", margin: "0 0 14px", fontSize: 34, lineHeight: 1.15, fontWeight: 600, letterSpacing: "-0.02em", color: "var(--text-primary)" }}
      >
        {release.title}
      </h1>
      <p className="fade-up" style={{ animationDelay: "0.12s", margin: "0 0 8px", fontSize: 18, lineHeight: 1.6, color: "var(--text-secondary)" }}>
        {release.summary}
      </p>

      {release.highlights?.length ? (
        <section className="fade-up" style={{ animationDelay: "0.15s", marginTop: 28 }}>
          <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 600, color: "var(--text-primary)" }}>
            {t.blog.release.whatsIn}
          </h2>
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {release.highlights.map((h, i) => (
              <li
                key={i}
                style={{ position: "relative", paddingLeft: 22, margin: "9px 0", fontSize: 16, lineHeight: 1.6, color: "var(--text-primary)" }}
              >
                <span style={{ position: "absolute", left: 2, top: 0, color: s.color }}>&bull;</span>
                {h}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="fade-up" style={{ animationDelay: "0.18s", display: "flex", flexWrap: "wrap", gap: 12, marginTop: 28 }}>
        <a
          href={tagUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-block", padding: "11px 24px", borderRadius: 999, fontSize: 14, fontWeight: 500, color: "#fff", backgroundColor: s.color, textDecoration: "none" }}
        >
          {t.blog.release.download.replace("{version}", version)}
        </a>
        {post && (
          <a
            href={`/blog/${post.slug}`}
            onClick={(e) => { e.preventDefault(); navigate(`/blog/${post.slug}`); }}
            style={{ display: "inline-block", padding: "11px 22px", borderRadius: 999, fontSize: 14, fontWeight: 500, color: "var(--text-secondary)", border: "1px solid var(--border-strong)", textDecoration: "none" }}
          >
            {t.blog.release.readStory}
          </a>
        )}
      </div>

      {/* Prev / next release nav */}
      <div
        style={{
          marginTop: 44,
          paddingTop: 20,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        {newer ? (
          <a href={`/blog/release/${newer.version}`} onClick={(e) => { e.preventDefault(); navigate(`/blog/release/${newer.version}`); }} style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            ← v{newer.version}
          </a>
        ) : <span />}
        {older ? (
          <a href={`/blog/release/${older.version}`} onClick={(e) => { e.preventDefault(); navigate(`/blog/release/${older.version}`); }} style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            v{older.version} →
          </a>
        ) : <span />}
      </div>
    </article>
  );
}

export function BlogPostPage({ slug }: { slug: string }) {
  const { locale } = useLocale();
  const t = useT();
  const post = getPost(locale, slug);

  useEffect(() => {
    document.title = post ? `${post.title} · CATE` : `${t.blog.post.notFoundTitle} · CATE`;
  }, [post, t]);

  if (!post) {
    return (
      <section className="px-6" style={{ maxWidth: 720, margin: "0 auto", paddingTop: 64, paddingBottom: 96, textAlign: "center" }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 }}>
          {t.blog.post.notFoundTitle}
        </h1>
        <p style={{ color: "var(--text-secondary)", marginBottom: 24 }}>
          {t.blog.post.notFoundBody}
        </p>
        <a
          href="/blog"
          onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
          style={{ color: "#5ba8f5", fontSize: 15 }}
        >
          {t.blog.post.notFoundBack}
        </a>
      </section>
    );
  }

  const { Content } = post;

  return (
    <article className="px-6" style={{ maxWidth: 720, margin: "0 auto", paddingTop: 24, paddingBottom: 96 }}>
      <a
        href="/blog"
        onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
        className="fade-up"
        style={{ display: "inline-block", marginBottom: 28, fontSize: 14, color: "var(--text-secondary)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
      >
        {t.blog.post.back}
      </a>

      <header className="fade-up" style={{ animationDelay: "0.05s" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <MetaLine post={post} />
          {post.tags?.length ? <span style={{ color: "var(--text-muted)" }}>·</span> : null}
          <TagText tags={post.tags} />
        </div>
        <h1
          style={{ margin: "0 0 16px", fontSize: 40, lineHeight: 1.15, fontWeight: 600, letterSpacing: "-0.02em", color: "var(--text-primary)" }}
        >
          {post.title}
        </h1>
      </header>

      {post.hero && (
        <img
          src={post.hero}
          alt={post.heroAlt || post.title}
          className="fade-up"
          style={{
            animationDelay: "0.1s",
            display: "block",
            width: "100%",
            height: "auto",
            borderRadius: 12,
            border: "1px solid var(--border-strong)",
            margin: "8px 0 8px",
          }}
        />
      )}

      <div className="fade-up" style={{ animationDelay: "0.15s" }}>
        <Content />
      </div>

      {/* Footer card — author + back link */}
      <div
        style={{
          marginTop: 40,
          padding: "18px 22px",
          borderRadius: 12,
          backgroundColor: "var(--surface-5)",
          border: "1px solid var(--border-strong)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {post.author ? `${t.blog.post.by} ${post.author} · ` : ""}{formatDate(locale, post.date)}
        </span>
        <a
          href="/blog"
          onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
          style={{ fontSize: 14, color: "#5ba8f5" }}
        >
          {t.blog.post.morePosts}
        </a>
      </div>

      <RelatedPosts slug={slug} />
    </article>
  );
}
